import { isEmpty } from "lodash";

const isTrackingEnabled = parseInt((typeof IS_ANALYTICS_TRACKING_ENABLED !== 'undefined' ? IS_ANALYTICS_TRACKING_ENABLED : '0'), 10);
const amplitudeApiKey = typeof AMPLITUDE_API_KEY !== 'undefined' ? AMPLITUDE_API_KEY : '';

let amplitudeInitPromise = null;
let lastIdentifiedUserId = null;
let amplitudeSdkPromise = null;

const canTrack = () => (__isBrowser__ && isTrackingEnabled && !!amplitudeApiKey);

const loadAmplitudeSdk = () => {
    if (!canTrack()) {
        return null;
    }

    if (!amplitudeSdkPromise) {
        amplitudeSdkPromise = import(/* webpackChunkName: "amplitudeSdkPromise" */ '@amplitude/analytics-browser');
    }

    return amplitudeSdkPromise;
};

const ensureInitialized = () => {
    if (!canTrack()) {
        return null;
    }

    if (!amplitudeInitPromise) {
        const sdkPromise = loadAmplitudeSdk();

        if (!sdkPromise) {
            return null;
        }

        amplitudeInitPromise = sdkPromise
            .then((sdk) => sdk.init(amplitudeApiKey, {
                minIdLength: 1,
                fetchRemoteConfig: false,
                defaultTracking: false
            }).promise)
            .catch((error) => {
                amplitudeInitPromise = null;
                throw error;
            });
    }

    return amplitudeInitPromise;
};

const buildPageProperties = () => ({
    page_title: typeof document !== 'undefined' ? document.title : '',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    url: typeof window !== 'undefined' ? window.location.href : ''
});

export default class AmplitudeHelper {

    /**
     * method used to track user events using amplitude
     * @param {String} event
     * @param {Object} trackedFields
     */
    trackEvent = (event = '', trackedFields = {}) => {
        if (!event || !canTrack()) {
            return;
        }

        const initPromise = ensureInitialized();
        if (!initPromise) {
            return;
        }

        initPromise
            .then(() => loadAmplitudeSdk())
            .then((sdk) => sdk && sdk.track(event, { ...trackedFields }))
            .catch((error) => {
                console.log('Amplitude, event tracking failed: ', error);
            });
    }

    /**
     * method used to identify new/updated user
     * @param {Number} user
     */
    identifyUser = (user) => {
        if (!canTrack() || isEmpty(user) || !user.is_loggedin || !user.user_id) {
            return;
        }

        const initPromise = ensureInitialized();
        if (!initPromise) {
            return;
        }

        initPromise.then(() => {
            const sdkPromise = loadAmplitudeSdk();

            if (!sdkPromise) {
                return;
            }

            return sdkPromise.then((sdk) => {
                const normalizedUserId = `${user.user_id}`;
                const currentAmplitudeUserId = sdk.getUserId();

                if (lastIdentifiedUserId === normalizedUserId || currentAmplitudeUserId === normalizedUserId) {
                    return;
                }

                sdk.setUserId(normalizedUserId);

                const amplitudeIdentify = new sdk.Identify();
                amplitudeIdentify.set('id', normalizedUserId);
                amplitudeIdentify.set('name', user.full_name || '');
                amplitudeIdentify.set('email', user.email || '');
                amplitudeIdentify.set('timestamp', new Date().toISOString());

                sdk.identify(amplitudeIdentify);
                lastIdentifiedUserId = normalizedUserId;
            });
        }).catch((error) => {
            console.log('Amplitude, identify user failed: ', error);
        });
    }

    /**
     * method used to identify which page the user is on
     */
    page = () => {
        if (!canTrack()) {
            return;
        }

        const initPromise = ensureInitialized();
        if (!initPromise) {
            return;
        }

        initPromise
            .then(() => loadAmplitudeSdk())
            .then((sdk) => sdk && sdk.track('Page Viewed', buildPageProperties()))
            .catch((error) => {
                console.log('Amplitude, page tracking failed: ', error);
            });
    }
}
